import { ChangeDetectorRef, Injectable, Pipe, PipeTransform } from '@angular/core';
import {
    StationCapabilitiesService,
    StationCapability,
    StationCapabilityCategory,
} from './station-capabilities.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

/**
 * Pipe to check if a charging station supports a certain ChargEye feature or function.
 * Performs Firmware version checks, OCPP connectivity checks and Hardware features in its determination.
 *
 * Usage: `stationId | isCapableOf:capability`
 *
 * Example:
 * ```
 * <div *ngIf="(stationId | isCapableOf:'SmartCharging')">Station is capable of Smart Charging</div>
 * ```
 */
@Pipe({
    name: 'isCapableOf',
    pure: false,
})
@UntilDestroy()
export class StationCapabilitiesPipe implements PipeTransform {
    stationId: string;
    capability: StationCapability;

    isCapable: boolean = undefined;
    isEvaluating = false;

    constructor(
        private stationCapabilitiesService: StationCapabilitiesService,
        private cdRef: ChangeDetectorRef
    ) {
        // if cache is cleared..
        this.stationCapabilitiesService.cacheCleared$.pipe(untilDestroyed(this)).subscribe(() => {
            // trigger re-evaluation
            this.evaluate();
        });
    }

    async evaluate() {
        if (this.isEvaluating) {
            return;
        }
        if (!this.stationId || !this.capability) {
            return;
        }
        try {
            this.isEvaluating = true;

            const res = await this.stationCapabilitiesService.isCapableOf(this.stationId, this.capability);
            this.isCapable = res;
            this.cdRef.markForCheck();
        } finally {
            this.isEvaluating = false;
        }
    }

    transform(stationId: string, capability: StationCapability): boolean {
        this.stationId = stationId;
        this.capability = capability;

        if (this.isCapable === undefined) {
            this.evaluate();
        }

        return this.isCapable;
    }
}

@Pipe({
    name: 'hasCapabilityCategory',
    pure: false,
})
@Injectable({
    providedIn: 'root',
})
@UntilDestroy()
export class HasCapabilityCategoryPipe implements PipeTransform {
    stationId: string;
    category: StationCapabilityCategory;

    result: boolean = undefined;
    isEvaluating = false;

    constructor(
        private stationCapabilitiesService: StationCapabilitiesService,
        private cdRef: ChangeDetectorRef
    ) {
        // if cache is cleared..
        this.stationCapabilitiesService.cacheCleared$.pipe(untilDestroyed(this)).subscribe(() => {
            // trigger re-evaluation
            this.evaluate();
        });
    }

    async evaluate() {
        if (this.isEvaluating) {
            return;
        }
        if (!this.stationId || !this.category) {
            return;
        }
        try {
            this.isEvaluating = true;

            const category = await this.stationCapabilitiesService.getStationCapabilityCategory(this.stationId);
            this.result = category.category === this.category;
            this.cdRef.markForCheck();
        } finally {
            this.isEvaluating = false;
        }
    }

    transform(stationId: string, category: StationCapabilityCategory): boolean {
        this.stationId = stationId;
        this.category = category;

        if (this.result === undefined) {
            this.evaluate();
        }

        return this.result;
    }
}
